import type { ParamValue } from '../graph/nodeDefs.js';
import type { ComponentDef, Scene } from '../scene/sceneState.js';
import {
  normalizeGraphLibrary,
  normalizeLayerDef,
  type GraphLibrary,
  type LayerDef,
  type LibraryGeometryDef,
  type LibraryMaterialDef,
  type ResolvedGraph,
} from '../scene/layerState.js';
import type { AppState, DocumentTypeDeclarations, EditorGraphMode, EditorGraphSlot, SceneFavorite, SvgPathShape } from './appState.js';
import { documentLightsOf, type DocumentLight } from '../graph/lights.js';
import type { DataGraphDef, DataGraphInstance, DataSourceDef, QueryGraphDef } from '../graph/dataGraph.js';
import type { EventGraphDef, RuntimeStateSchema } from '../graph/eventGraph.js';

export interface AppStateSnapshot {
  documentId: string;
  params: Record<string, ParamValue>;
  scene: Scene;
  editorGraphSlot: EditorGraphSlot;
  editorGraphMode: EditorGraphMode;
  editorGraphKind: AppState['editorGraphKind'];
  activeDataGraphId: string | null;
  activeEventGraphId: string | null;
  activeQueryGraphId: string | null;
  componentLibrary: Record<string, ComponentDef>;
  layerLibrary: Record<string, LayerDef>;
  sceneFavorites: SceneFavorite[];
  geometryLibrary: GraphLibrary<LibraryGeometryDef>['user'];
  materialLibrary: GraphLibrary<LibraryMaterialDef>['user'];
  shapes: Record<string, SvgPathShape>;
  dataGraphs: Record<string, DataGraphDef>;
  queryGraphs: Record<string, QueryGraphDef>;
  dataSources: Record<string, DataSourceDef>;
  data: DataGraphInstance | null;
  runtimeState: RuntimeStateSchema;
  eventGraphs: Record<string, EventGraphDef>;
  lights: DocumentLight[];
  types: DocumentTypeDeclarations;
}

export type FileDocumentSnapshot = Pick<
  AppStateSnapshot,
  | 'params'
  | 'documentId'
  | 'scene'
  | 'componentLibrary'
  | 'layerLibrary'
  | 'sceneFavorites'
  | 'geometryLibrary'
  | 'materialLibrary'
  | 'shapes'
  | 'dataGraphs'
  | 'queryGraphs'
  | 'dataSources'
  | 'data'
  | 'runtimeState'
  | 'eventGraphs'
  | 'lights'
  | 'types'
>;

export type FileWorkspaceSnapshot = Pick<
  AppStateSnapshot,
  | 'editorGraphSlot'
  | 'editorGraphMode'
  | 'editorGraphKind'
  | 'activeDataGraphId'
  | 'activeEventGraphId'
  | 'activeQueryGraphId'
>;

function requireObjectRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

function parseComponentLibrary(value: unknown): Record<string, ComponentDef> {
  const record = requireObjectRecord(value ?? {}, 'componentLibrary');
  return Object.fromEntries(
    Object.entries(record).map(([id, comp]) => [id, comp as ComponentDef]),
  );
}

function parseLayerLibrary(value: unknown): Record<string, LayerDef> {
  const record = requireObjectRecord(value ?? {}, 'layerLibrary');
  return Object.fromEntries(
    Object.entries(record).map(([id, entry]) => {
      const normalized = normalizeLayerDef(entry as LayerDef);
      return [id, normalized];
    }),
  );
}

function parseGraphLibrary<T extends ResolvedGraph>(
  value: unknown,
  label: string,
): Record<string, T> {
  const record = requireObjectRecord(value ?? {}, label) as Record<string, T>;
  return normalizeGraphLibrary(record);
}

function parseSceneFavorites(value: unknown): SceneFavorite[] {
  if (value === undefined) return [];
  const items = Array.isArray(value) ? value : [];
  return items.flatMap(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const favorite = item as Partial<SceneFavorite>;
    if (typeof favorite.id !== 'string' || !favorite.target) return [];
    return [{ id: favorite.id, target: favorite.target }];
  });
}

function parseEditorGraphSlot(value: unknown): EditorGraphSlot {
  if (value === 'material') return value;
  return 'geometry';
}

function parseEditorGraphMode(value: unknown): EditorGraphMode {
  if (value === 'geometry' || value === 'material') return value;
  return 'auto';
}

function parseEditorGraphKind(value: unknown): AppState['editorGraphKind'] {
  return value === 'material' || value === 'data' || value === 'event' ? value : 'geometry';
}

function parseDocumentId(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error('Invalid documentId');
  }
  return value;
}

export function createStateSnapshot(appState: AppState): AppStateSnapshot {
  return {
    documentId: appState.documentId,
    params: appState.params,
    scene: appState.scene,
    editorGraphSlot: appState.editorGraphSlot,
    editorGraphMode: appState.editorGraphMode,
    editorGraphKind: appState.editorGraphKind,
    activeDataGraphId: appState.activeDataGraphId,
    activeEventGraphId: appState.activeEventGraphId,
    activeQueryGraphId: appState.activeQueryGraphId,
    componentLibrary: appState.componentLibrary,
    layerLibrary: appState.layerLibrary,
    sceneFavorites: appState.sceneFavorites,
    geometryLibrary: appState.geometryLibrary.user,
    materialLibrary: appState.materialLibrary.user,
    shapes: appState.shapes,
    dataGraphs: appState.dataGraphs,
    queryGraphs: appState.queryGraphs,
    dataSources: appState.dataSources,
    data: appState.data,
    runtimeState: appState.runtimeState,
    eventGraphs: appState.eventGraphs,
    lights: appState.lights,
    types: appState.types,
  };
}

export function createFileDocumentSnapshot(appState: AppState): FileDocumentSnapshot {
  const snap = createStateSnapshot(appState);
  return {
    documentId: snap.documentId,
    params: snap.params,
    scene: snap.scene,
    componentLibrary: snap.componentLibrary,
    layerLibrary: snap.layerLibrary,
    sceneFavorites: snap.sceneFavorites,
    geometryLibrary: snap.geometryLibrary,
    materialLibrary: snap.materialLibrary,
    shapes: snap.shapes,
    dataGraphs: snap.dataGraphs,
    queryGraphs: snap.queryGraphs,
    dataSources: snap.dataSources,
    data: snap.data,
    runtimeState: snap.runtimeState,
    eventGraphs: snap.eventGraphs,
    lights: snap.lights,
    types: snap.types,
  };
}

export function createFileWorkspaceSnapshot(appState: AppState): FileWorkspaceSnapshot {
  const snap = createStateSnapshot(appState);
  return {
    editorGraphSlot: snap.editorGraphSlot,
    editorGraphMode: snap.editorGraphMode,
    editorGraphKind: snap.editorGraphKind,
    activeDataGraphId: snap.activeDataGraphId,
    activeEventGraphId: snap.activeEventGraphId,
    activeQueryGraphId: snap.activeQueryGraphId,
  };
}

export function restoreStateSnapshot(appState: AppState, value: unknown): void {
  const snap = requireObjectRecord(value, 'state snapshot');

  appState.documentId = parseDocumentId(snap.documentId);
  appState.params = (snap.params as Record<string, ParamValue>) || {};
  appState.scene = (snap.scene as Scene) || appState.scene;
  appState.editorGraphSlot = parseEditorGraphSlot(snap.editorGraphSlot);
  appState.editorGraphMode = parseEditorGraphMode(snap.editorGraphMode);
  appState.editorGraphKind = parseEditorGraphKind(snap.editorGraphKind);
  appState.activeDataGraphId = typeof snap.activeDataGraphId === 'string' ? snap.activeDataGraphId : null;
  appState.inspectorTarget = { type: 'scene' };
  appState.activeEventGraphId = typeof snap.activeEventGraphId === 'string' ? snap.activeEventGraphId : null;
  appState.activeQueryGraphId = typeof snap.activeQueryGraphId === 'string' ? snap.activeQueryGraphId : null;
  appState.viewportMode = 'scene';
  appState.viewportIsolationTarget = null;
  appState.selectedNode = null;

  if (snap.layerLibrary) {
    appState.componentLibrary = parseComponentLibrary(snap.componentLibrary);
    appState.layerLibrary = parseLayerLibrary(snap.layerLibrary);
  } else {
    appState.componentLibrary = {};
    appState.layerLibrary = {};
  }

  appState.sceneFavorites = parseSceneFavorites(snap.sceneFavorites);
  appState.activeComponentId = null;
  appState.geometryLibrary.user = parseGraphLibrary<LibraryGeometryDef>(snap.geometryLibrary, 'geometryLibrary');
  appState.materialLibrary.user = parseGraphLibrary<LibraryMaterialDef>(snap.materialLibrary, 'materialLibrary');
  appState.shapes = (snap.shapes as Record<string, SvgPathShape>) ?? {};
  appState.dataGraphs = (snap.dataGraphs as Record<string, DataGraphDef>) ?? {};
  appState.queryGraphs = (snap.queryGraphs as Record<string, QueryGraphDef>) ?? {};
  appState.dataSources = (snap.dataSources as Record<string, DataSourceDef>) ?? {};
  appState.data = (snap.data as DataGraphInstance | null) ?? null;
  appState.runtimeState = (snap.runtimeState as RuntimeStateSchema) ?? {};
  appState.eventGraphs = (snap.eventGraphs as Record<string, EventGraphDef>) ?? {};
  appState.lights = documentLightsOf(snap.lights);
  appState.types = snap.types && typeof snap.types === 'object' && !Array.isArray(snap.types)
    ? snap.types as DocumentTypeDeclarations
    : {};

  appState.editorContext = {
    mode: 'layer',
    componentId: undefined,
    layerId: undefined,
    layerSlot: appState.editorGraphSlot,
  };
  appState.nodes = [];
  appState.edges = [];
  appState.editorGraphDirty = false;
}
